import { useEffect, useMemo, useState } from "react";
import "../../../css/CheckDetails/PEPSanctions/PEPSanctions.css";
import StatusButton from "../../Components/Buttons/StatusButton";
import Banner from "../../Components/Banners/Banner";
import PepSanMatchReasonDropdown from "../../Components/Dropdown/PepSanMatchReasonDropdown";
import { ReactComponent as ArrowUp } from "../../../assets/icons/arrowUp.svg";
import { ReactComponent as WarningIcon } from "../../../assets/icons/warning.svg";
import { ReactComponent as CloseIcon } from "../../../assets/icons/rowClose.svg";
import dayjs from "dayjs";
import {
  NON_MATCH_REASONS,
  TYPE_LABELS,
  TYPE_DISPLAY_ORDER,
  MY_CLIENT_REASON,
  RCA_NON_MATCH_REASONS,
} from "../Utils/getConstants";
import { getDecisionForService } from "../Utils/Experian/getServiceDecision";
import experianServiceNames from "../Utils/Experian/getExperianServiceNames";
import capitalizeFirst from "../../Checks/utils/factories/capitalise";
import {
  saveReasonOverride,
  clearReasonOverride,
} from "../Utils/Experian/pepSanReasonOverrides";
import { colorForResult } from "../Utils/getColorForDecision";
import { subscribePepSanReasonsByRef } from "../Utils/Experian/pepSanReasonsStore";
import {
  getAllMatchesResolved,
  anyMatchIsMyClient,
} from "../Utils/Experian/allMatchesHaveReasons";
import YesButton from "../../Components/Buttons/YesButton";
import ToggleSwitch from "../../Components/Buttons/ToggleSwitch";
import { autoPopulatePepSanReasons } from "../Utils/Experian/autoPopulatePepSanReasons";

// ---------------- helpers ----------------
const safe = (v, fb = "-") =>
  v === null || v === undefined || v === "" ? fb : v;

const fmtDate = (v) => {
  if (!v) return "-";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY") : String(v);
};

const criteriaForType = (match, type) => {
  const list = Array.isArray(match?.matchingCriteria)
    ? match.matchingCriteria
    : [];
  return list.filter((c) => String(c?.searchType || "").toUpperCase() === type);
};

const pickPrimaryName = (names = []) => {
  if (!Array.isArray(names) || !names.length) return null;
  return (
    names.find((n) =>
      String(n?.type || "")
        .toLowerCase()
        .includes("primary"),
    ) || names[0]
  );
};

const joinName = (n) => {
  if (!n) return "-";
  return `${safe(n.firstName, "")} ${safe(n.surname, "")}`.trim() || "-";
};

const stringifyRegionList = (criteria) => {
  const regions = criteria.map((c) => c?.region).filter(Boolean);
  return regions.length ? regions.join(", ") : "-";
};

const prettifyKey = (k) =>
  String(k || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const objectToPairs = (obj) => {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj)
    .filter(
      ([k, v]) => k && v !== null && v !== undefined && String(v).trim() !== "",
    )
    .map(([k, v]) => ({ key: String(k), value: v }));
};

const uniq = (arr) => [...new Set((arr || []).filter(Boolean))];

const getCriteriaExtras = (criteria = []) => {
  const regions = uniq(criteria.map((c) => c?.region));
  const relationships = uniq(criteria.map((c) => c?.relationship));
  const relatedToIds = uniq(criteria.map((c) => c?.relatedToId));
  return {
    regionsText: regions.length ? regions.join(", ") : "-",
    relationshipsText: relationships.length ? relationships.join(", ") : "-",
    relatedToIdsText: relatedToIds.length ? relatedToIds.join(", ") : "-",
  };
};

// ---------------- Component ----------------
const PepSanctions = ({ response, checkId, ownerEmail, dob }) => {
  const [open, setOpen] = useState(false);
  const [openCards, setOpenCards] = useState({});
  const [activeTab, setActiveTab] = useState("SAN");
  const [openReasonRow, setOpenReasonRow] = useState(null);

  // console.log(canConfirmRCA ? "User can confirm RCA reasons" : "User cannot confirm RCA reasons");
  // console.log("User Role:", userRole);
  // console.log("User Permissions:", userPermissions);

  const [localResponse, setLocalResponse] = useState(response);

  // local optimistic override (instant UI)
  const [rowReasonMap, setRowReasonMap] = useState({});

  // Firestore truth (also used for report later)
  const [fbReasonsByRef, setFbReasonsByRef] = useState({});
  const toggleCard = (ref) => {
    setOpenCards((prev) => ({ ...prev, [ref]: !prev?.[ref] }));
  };

  // subscribe to Firestore reasons
  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    if (!ownerEmail || !checkId) return;

    (async () => {
      try {
        unsub = await subscribePepSanReasonsByRef(
          ownerEmail,
          checkId,
          (next) => {
            if (!cancelled) setFbReasonsByRef(next || {});
          },
        );
      } catch (e) {
        // console.error("[PepSanctions] subscribe reasons failed:", e);
        if (!cancelled) setFbReasonsByRef({});
      }
    })();

    return () => {
      cancelled = true;
      if (typeof unsub === "function") unsub();
    };
  }, [ownerEmail, checkId, response, dob]);

  const normalizeReasons = (reasons) => {
    if (!Array.isArray(reasons)) return [];
    if (typeof reasons[0] === "string") return reasons.filter(Boolean);
    return reasons.flatMap((r) => r?.details ?? []).filter(Boolean);
  };

  useEffect(() => {
    setLocalResponse(response);
    const reasons = autoPopulatePepSanReasons({
      results: response?.clientResponsePayload?.decisionElements?.find((d) =>
        ["uk-pepsanc", "watchlist", "watchlistscreening"].includes(
          String(d?.serviceName || "").toLowerCase(),
        ),
      )?.otherData?.response?.searchResults?.[0]?.results,
      applicantDob: dayjs(dob).isValid()
        ? dayjs(dob).format("YYYY-MM-DD")
        : null,
    });

    // console.log("Auto-populated reasons on response change:", reasons);
    try {
      Object.entries(reasons).forEach(([ref, reasonList]) => {
        saveReasonOverride({
          ownerEmail,
          checkId,
          ref,
          reasons: reasonList,
        });
      });
    } catch (e) {
      console.error("Failed to save reason overrides:", e);
    }
  }, [response]);

  // EFFECTIVE reasons:
  // 1) optimistic local
  // 2) Firestore
  const getEffectiveReasons = ({ ref }) => {
    const local = rowReasonMap?.[ref];
    if (Array.isArray(local)) return local;

    const entry = fbReasonsByRef?.[ref];
    return Array.isArray(entry?.reasons) ? entry.reasons : [];
  };

  const setReasonsForRow = ({ ref }, nextReasons) => {
    setRowReasonMap((prev) => ({ ...prev, [ref]: nextReasons }));
  };

  const clearReasonsForRow = ({ ref }) => {
    setRowReasonMap((prev) => ({ ...prev, [ref]: [] }));
  };

  // -------- pull from JSON structure --------
  const aliases = Array.isArray(experianServiceNames.PEP_SANCTIONS)
    ? experianServiceNames.PEP_SANCTIONS
    : [experianServiceNames.PEP_SANCTIONS];

  const element = useMemo(() => {
    const els = localResponse?.clientResponsePayload?.decisionElements || [];
    return els.find((d) =>
      aliases.some(
        (s) => (s || "").toLowerCase() === (d?.serviceName || "").toLowerCase(),
      ),
    );
  }, [localResponse, aliases]);

  const availableTypes = useMemo(() => {
    const fromMatches = Array.isArray(element?.matches)
      ? element.matches
          .map((m) => String(m?.name || "").toUpperCase())
          .filter(Boolean)
      : [];

    if (fromMatches.length) return [...new Set(fromMatches)];

    const results =
      element?.otherData?.response?.searchResults?.[0]?.results || [];
    const inferred = [];
    results.forEach((r) => {
      (r?.matchingCriteria || []).forEach((c) => {
        const t = String(c?.searchType || "").toUpperCase();
        if (t) inferred.push(t);
      });
    });

    const uniq = [...new Set(inferred)];
    return uniq.length ? uniq : ["SAN", "PEP", "RCA"];
  }, [element]);

  const orderedTypes = useMemo(() => {
    const list = [...availableTypes];
    return list.sort((a, b) => {
      const ia = TYPE_DISPLAY_ORDER.indexOf(a);
      const ib = TYPE_DISPLAY_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [availableTypes]);

  useEffect(() => {
    if (!orderedTypes.length) return;
    if (!orderedTypes.includes(activeTab)) setActiveTab(orderedTypes[0]);
  }, [orderedTypes, activeTab]);

  const results = useMemo(() => {
    return element?.otherData?.response?.searchResults?.[0]?.results || [];
  }, [element]);

  // cards
  const cards = useMemo(() => {
    const counters = {};
    const out = [];

    results.forEach((match) => {
      const types = Array.isArray(match?.matchingCriteria)
        ? [
            ...new Set(
              match.matchingCriteria.map((c) =>
                String(c?.searchType || "").toUpperCase(),
              ),
            ),
          ]
        : [];

      types.forEach((t) => {
        counters[t] = (counters[t] || 0) + 1;
        out.push({
          type: t || "UNK",
          ref: `${t || "UNK"}${counters[t] || 1}`, // (note: display-key, but you’re storing with this currently)
          match,
        });
      });
    });

    return out;
  }, [results]);

  const filteredCards = useMemo(
    () => cards.filter((c) => c.type === activeTab),
    [cards, activeTab],
  );

  const activeTabRefs = useMemo(
    () => filteredCards.map((c) => c.ref),
    [filteredCards],
  );

  const expandAllActiveTab = () => {
    setOpenCards((prev) => {
      const next = { ...prev };
      activeTabRefs.forEach((ref) => (next[ref] = true));
      return next;
    });
  };

  const collapseAllActiveTab = () => {
    setOpenCards((prev) => {
      const next = { ...prev };
      activeTabRefs.forEach((ref) => (next[ref] = false));
      return next;
    });
  };

  const allExpandedInActiveTab = useMemo(() => {
    if (!activeTabRefs.length) return false;
    return activeTabRefs.every((ref) => !!openCards?.[ref]);
  }, [activeTabRefs, openCards]);

  const toggleExpandCollapseAll = (nextChecked) => {
    setOpenCards((prev) => {
      const next = { ...prev };
      activeTabRefs.forEach((ref) => {
        next[ref] = nextChecked;
      });
      return next;
    });
  };

  useEffect(() => {
    setOpenCards({});
  }, [activeTab]);

  const hasUnresolvedByType = useMemo(() => {
    const map = {};
    cards.forEach((c) => {
      const reasons = getEffectiveReasons({ ref: c.ref });
      const hasReasons = reasons.length > 0;

      if (!map[c.type]) map[c.type] = false;
      if (!hasReasons) map[c.type] = true;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, rowReasonMap, fbReasonsByRef]);

  const rawDecision = getDecisionForService(
    localResponse,
    experianServiceNames.PEP_SANCTIONS,
  );
  const decision = rawDecision
    ? capitalizeFirst(String(rawDecision).toLowerCase())
    : null;
  const providerLower = rawDecision ? String(rawDecision).toLowerCase() : null;

  const allCardsHaveReasons = useMemo(() => {
    return getAllMatchesResolved(cards, (ref) => getEffectiveReasons({ ref }));
  }, [cards, rowReasonMap, fbReasonsByRef, getEffectiveReasons]);
  useEffect(() => setLocalResponse(response), [response]);

  const anyCardIsMyClient = useMemo(() => {
    return anyMatchIsMyClient(
      cards,
      (ref) => getEffectiveReasons({ ref }),
      MY_CLIENT_REASON,
    );
  }, [cards, rowReasonMap, fbReasonsByRef, getEffectiveReasons]);
  //  UI decision (override when all reasons present)
  const uiDecisionLower = allCardsHaveReasons
    ? anyCardIsMyClient
      ? "referred"
      : "approved"
    : providerLower;
  const uiDecision = uiDecisionLower ? capitalizeFirst(uiDecisionLower) : null;

  if (!uiDecision) return null;
  return (
    <div className="Result-Section">
      <div
        className="Result-Section-Header"
        onClick={() => setOpen((prev) => !prev)}
        // onKeyDown={(e) =>
        //   (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)
        // }
      >
        <div className="AML-Text-Subtitle">PEP &amp; Sanctions</div>

        <div className="Result-Section-Row">
          {decision && (
            <StatusButton
              type={
                uiDecision === "Declined"
                  ? "Action Required"
                  : uiDecision || "Not Started"
              }
              color={colorForResult(
                uiDecision === "Declined"
                  ? "Action Required"
                  : uiDecision || "Not Started",
              )}
            />
          )}

          <ArrowUp
            className={`collapse-icon ${open ? "collapsed" : ""}`}
            role="button"
            tabIndex={0}
            aria-expanded={open}
            // onClick={() => setOpen((p) => !p)}
            // onKeyDown={(e) =>
            //   (e.key === "Enter" || e.key === " ") && setOpen((p) => !p)
            // }
          />
        </div>
      </div>

      <div className={`rs-body ${!open ? "is-collapsed" : ""}`}>
        <div className="rs-body-inner" style={{ gap: 0 }}>
          {open && (
            <>
              {decision === "Approved" && (
                <div style={{ padding: "0 16px 16px 16px" }}>
                  <Banner
                    type={
                      uiDecision === "Approved"
                        ? "success"
                        : uiDecision === "Referred"
                          ? "warning"
                          : "info"
                    }
                    message={
                      uiDecision === "Approved"
                        ? "No PEP or Sanctions records found."
                        : uiDecision === "Referred"
                          ? "Matches found. Please review the details below and refer to compliance if necessary."
                          : "The decision is pending. Please review all matches and provide reasons to reach a final decision."
                    }
                  />
                </div>
              )}
              {activeTab === "RCA" && (
                <>
                  <div style={{ padding: "0px 16px" }}>
                    <Banner
                      type="error"
                      message={
                        <>
                          <strong>IMPORTANT -</strong> Under UK Money Laundering
                          Regulations, you must document why the Relative or
                          Close Associate (RCA) identified through screening is
                          not a match to your client.
                        </>
                      }
                    />
                  </div>
                  <div className="ResultSesion-GapBox" />
                </>
              )}

              {/* Tabs */}
              <div className="amv-tabs">
                {orderedTypes.map((id) => {
                  const showWarning = hasUnresolvedByType[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`amv-tab ${activeTab === id ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab(id);
                        setOpenReasonRow(null);
                      }}
                    >
                      <div className="PepSanctions-TabContent">
                        {showWarning && (
                          <span className="PepSanctions-TabIcon">
                            <WarningIcon />
                          </span>
                        )}
                        <span className="AML-Text-Input">
                          {TYPE_LABELS[id]?.label ?? id}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {filteredCards.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    padding: "0 24px",
                    marginTop: 16,
                  }}
                >
                  <ToggleSwitch
                    checked={allExpandedInActiveTab}
                    disabled={!filteredCards.length}
                    onChange={toggleExpandCollapseAll}
                    label={
                      "Expand all"
                      // allExpandedInActiveTab ? "Collapse all" : "Expand all"
                    }
                  />
                </div>
              )}

              {/* Cards */}
              <div className="pep-san-cards">
                {filteredCards.map((c) => (
                  <PepSanMatchCard
                    key={`${c.type}-${c.ref}-${
                      c.match?.record?.profileId ?? ""
                    }`}
                    card={c}
                    openReasonRow={openReasonRow}
                    setOpenReasonRow={setOpenReasonRow}
                    getEffectiveReasons={getEffectiveReasons}
                    setReasonsForRow={setReasonsForRow}
                    clearReasonsForRow={clearReasonsForRow}
                    normalizeReasons={normalizeReasons}
                    checkId={checkId}
                    ownerEmail={ownerEmail}
                    isExpanded={!!openCards[c.ref]} // new
                    onToggleExpand={() => toggleCard(c.ref)}
                    fbReasonsByRef={fbReasonsByRef}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        {/* <div className="verticialSizedBox" /> */}
      </div>
    </div>
  );
};

export default PepSanctions;

// ---------------- Card ----------------
function PepSanMatchCard({
  card,
  openReasonRow,
  setOpenReasonRow,
  getEffectiveReasons,
  setReasonsForRow,
  clearReasonsForRow,
  normalizeReasons,
  checkId,
  ownerEmail,
  isExpanded,
  onToggleExpand,
  fbReasonsByRef,
}) {
  const { type, ref, match } = card;
  const record = match?.record || {};
  const countryPairs = useMemo(
    () => objectToPairs(record?.countryDetails),
    [record?.countryDetails],
  );

  const names = Array.isArray(record?.names) ? record.names : [];
  const primary = pickPrimaryName(names);
  const displayName = joinName(primary);

  const criteriaForThisType = criteriaForType(match, type);
  const { regionsText, relationshipsText, relatedToIdsText } = useMemo(
    () => getCriteriaExtras(criteriaForThisType),
    [criteriaForThisType],
  );

  const reasonStrings = getEffectiveReasons({ ref });
  const hasReasons = reasonStrings.length > 0;
  const isMyClient = reasonStrings.some((r) =>
    MY_CLIENT_REASON.some(
      (x) => String(x).trim().toLowerCase() === String(r).trim().toLowerCase(),
    ),
  );

  const sanctions = Array.isArray(record?.sanctions) ? record.sanctions : [];
  const sources = Array.isArray(record?.sources) ? record.sources : [];
  const roles = Array.isArray(record?.roles) ? record.roles : [];
  const descriptions = Array.isArray(record?.descriptions)
    ? record.descriptions
    : [];

  const reasonStatus = !hasReasons
    ? "unresolved"
    : isMyClient
      ? "refer"
      : "not-my-client";

  const isRCA = String(type || "").toUpperCase() === "RCA";
  const canConfirmRCA = true;
  const canShowViewOptions =
    !hasReasons && (!isRCA || (isRCA && canConfirmRCA));

  const handleConfirmReasons = async (next, extraData = {}) => {
    const normalized = normalizeReasons(next);

    // optimistic UI
    setReasonsForRow({ ref }, normalized);
    setOpenReasonRow(null);

    try {
      await saveReasonOverride({
        ownerEmail,
        checkId,
        ref,
        reasons: normalized,
        rcaDetails: extraData?.rcaDetails || "",
      });
      // UI will also stay correct because snapshot will update fbReasonsByRef
    } catch (e) {
      // revert
      setReasonsForRow({ ref }, []);
    }
  };

  const handleClearReasons = async () => {
    // optimistic UI
    clearReasonsForRow({ ref });
    if (openReasonRow === ref) setOpenReasonRow(null);

    try {
      await clearReasonOverride({ ownerEmail, checkId, ref });
    } catch (e) {
      // if you want, you could refetch here; snapshot will usually restore anyway
    }
  };

  return (
    <div className={`pep-san-card pep-san-card--${reasonStatus}`}>
      <div
        className="pep-san-card-header"
        onClick={(e) => {
          if (e.target.closest("[data-no-toggle]")) return; // ignore clicks in reasons area
          onToggleExpand();
        }}
        // onKeyDown={(e) => {
        //   if (e.key === "Enter" || e.key === " ") {
        //     e.stopPropagation();
        //     onToggleExpand();
        //   }
        // }}
      >
        {/* TOP ROW: only REF + ARROW */}
        <div className="pep-san-header-top">
          <div className="pep-san-ref">
            <span className="pep-san-type-pill AML-Text-Input">{ref}</span>
          </div>

          <ArrowUp
            className={`pep-san-expand-icon ${isExpanded ? "expanded" : ""}`}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
          />
        </div>

        {/* SECOND ROW: name/meta + reasons */}
        <div
          className={`pep-san-header-main ${!isExpanded ? "is-collapsed" : ""}`}
        >
          <div className="pep-san-card-left">
            <div className="AML-Text-Subtitle">
              {String(type || "").toUpperCase() === "RCA" ? (
                <>
                  {safe(relationshipsText)}{" "}
                  <span className="AML-Text-Body">
                    (Relationship to <b>{displayName}</b>)
                  </span>
                  {/* <span className="AML-Text-Body">
                      <b>Related To ID:</b> {safe(relatedToIdsText)}
                    </span> */}
                </>
              ) : (
                displayName
              )}
            </div>

            <div className="pep-san-meta">
              {/* {String(type || "").toUpperCase() === "RCA" && (
                  <span className="AML-Text-Body">
                    <b>Name:</b> {safe(displayName)}
                  </span>
                )} */}
              <span className="AML-Text-Body">
                <b>Region:</b> {safe(regionsText)}
              </span>

              <span className="AML-Text-Body">
                <b>DOB:</b> {fmtDate(record?.dateOfBirth)}
              </span>
              <span className="AML-Text-Body">
                <b>Status:</b> {safe(record?.activeStatus)}
              </span>
              <span className="AML-Text-Body">
                <b>Deceased:</b>{" "}
                {record?.deceased === undefined || record?.deceased === null
                  ? "-"
                  : capitalizeFirst(String(record.deceased))}
              </span>

              <span className="AML-Text-Body">
                <b>Gender:</b> {capitalizeFirst(safe(record?.gender))}
              </span>
            </div>
          </div>
          <div className="pep-san-card-right" data-no-toggle>
            {canShowViewOptions ? (
              <div className="pep-san-reason-anchor">
                <YesButton
                  buttonText="View Options"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenReasonRow(openReasonRow === ref ? null : ref);
                  }}
                />

                {openReasonRow === ref && (
                  <PepSanMatchReasonDropdown
                    onClose={() => setOpenReasonRow(null)}
                    reasons={
                      String(type || "").toUpperCase() === "RCA"
                        ? RCA_NON_MATCH_REASONS
                        : NON_MATCH_REASONS
                    }
                    value={getEffectiveReasons({ ref })}
                    isRCA={String(type || "").toUpperCase() === "RCA"}
                    existingRcaDetails={fbReasonsByRef?.[ref]?.rcaDetails || ""}
                    onConfirm={handleConfirmReasons}
                  />
                )}
              </div>
            ) : (
              <div className="pep-san-reason-chip">
                <span
                  className="AML-Text-Input"
                  style={
                    String(type || "").toUpperCase() === "RCA"
                      ? !isMyClient
                        ? { color: "var(--Success)" }
                        : { color: "var(--Error)" }
                      : !isMyClient
                        ? { color: "var(--Success)" }
                        : { color: "var(--Error)" }
                  }
                >
                  {String(type || "").toUpperCase() === "RCA"
                    ? isMyClient
                      ? "Refer to compliance"
                      : "Not a match"
                    : isMyClient
                      ? "Refer to compliance"
                      : "Not my client"}
                </span>

                <CloseIcon
                  className="not-my-client-icon"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearReasons();
                  }}
                  // onKeyDown={(e) => {
                  //   if (e.key === "Enter" || e.key === " ") {
                  //     e.stopPropagation();
                  //     handleClearReasons();
                  //   }
                  // }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`pep-san-card-body ${!isExpanded ? "is-collapsed" : ""}`}>
        {!!descriptions.length && (
          <div className="pep-san-block">
            <div className="AML-Text-Body pep-san-block-title">
              <b>Descriptions</b>
            </div>
            <div className="pep-san-block-list">
              {descriptions.map((d, i) => (
                <div key={i} className="AML-Text-Body">
                  • <b>{safe(d?.recordType)}:</b> {safe(d?.description)}
                </div>
              ))}
            </div>
          </div>
        )}

        {!!roles.length && (
          <div className="pep-san-block">
            <div className="AML-Text-Body pep-san-block-title">
              <b>Roles</b>
            </div>
            <div className="pep-san-block-list">
              {roles.map((r, i) => (
                <div key={i} className="AML-Text-Body">
                  • <b>{safe(r?.type)}:</b> {safe(r?.title)}{" "}
                  {r?.category ? (
                    <span style={{ opacity: 0.7 }}>({r.category})</span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {hasReasons && (
          <div className="pep-san-block">
            <div className="AML-Text-Body pep-san-block-title">
              <b>Reasons</b>
            </div>
            <div className="pep-san-block-list">
              {reasonStrings.map((r) => (
                <div key={r} className="AML-Text-Body">
                  • {r}
                </div>
              ))}

              {String(type || "").toUpperCase() === "RCA" &&
                fbReasonsByRef?.[ref]?.rcaDetails && (
                  <div className="pep-san-chip-wrap">
                    <span key={ref} className="pep-san-chip AML-Text-Body">
                      Details: {fbReasonsByRef[ref].rcaDetails}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}

        <div className="pep-san-two-col">
          <div className="pep-san-block">
            <div className="AML-Text-Body pep-san-block-title">
              <b>Names / Aliases</b>
            </div>
            <div className="pep-san-chip-wrap">
              {names.map((n, i) => (
                <span key={i} className="pep-san-chip AML-Text-Body">
                  {safe(n?.type)}: {safe(n?.firstName)} {safe(n?.surname)}
                </span>
              ))}
            </div>
            <div style={{ height: 12 }} />
            {!!countryPairs.length && (
              <>
                {/* optional if you have it */}
                <div className="AML-Text-Body pep-san-block-title">
                  <b>Country Details</b>
                </div>
                <div className="pep-san-chip-wrap">
                  {countryPairs.map((p) => (
                    <span key={p.key} className="pep-san-chip AML-Text-Body">
                      {prettifyKey(p.key)}: {safe(p.value)}
                    </span>
                  ))}
                </div>
              </>
            )}

            {!!sources.length && (
              <>
                <div style={{ height: 12 }} />

                <div className="AML-Text-Body pep-san-block-title">
                  <b>Sources</b>
                </div>

                <div className="pep-san-chip-wrap">
                  {sources.map((src, i) => {
                    const value = String(src || "").trim();
                    const isUrl = value.startsWith("http");

                    let domain = "";
                    if (isUrl) {
                      try {
                        domain = new URL(value).hostname.replace(/^www\./, "");
                      } catch {
                        domain = `Source ${i + 1}`;
                      }
                    }

                    return isUrl ? (
                      <a
                        key={i}
                        href={value}
                        target="_blank"
                        rel="noreferrer"
                        className="pep-san-chip AML-Text-Body pep-san-source-link"
                        title={value}
                      >
                        Link: {domain || `Source ${i + 1}`}
                      </a>
                    ) : (
                      <span key={i} className="pep-san-chip AML-Text-Body">
                        Source: {value}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="pep-san-block">
            {!!sanctions.length && (
              <>
                <div className="AML-Text-Body pep-san-block-title">
                  <b>Sanctions</b>
                </div>
                <div className="pep-san-block-list">
                  {sanctions.map((s, i) => (
                    <div key={i} className="pep-san-sanction-card">
                      <div className="AML-Text-Body">
                        <b>{safe(s?.name)}</b>
                      </div>
                      <div className="AML-Text-Body" style={{ opacity: 0.8 }}>
                        {safe(s?.region)} • {safe(s?.status)} •{" "}
                        {safe(s?.listProviderCode)} • Code {safe(s?.code)}
                      </div>
                      <div className="AML-Text-Body" style={{ opacity: 0.8 }}>
                        Since: {safe(s?.sinceDay, "")} {safe(s?.sinceMonth, "")}{" "}
                        {safe(s?.sinceYear)}
                        {s?.toMonth || s?.toYear || s?.toDay ? (
                          <>
                            {" "}
                            • To: {safe(s?.toDay, "")} {safe(s?.toMonth, "")}{" "}
                            {safe(s?.toYear, "")}
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
